import React, { useEffect, useRef, useState } from "react";
import "./counter.css";

import { gsap } from "gsap";
import { useLanguage } from "./context/LanguageContext";
import translations from "./locals/translations.json";
import { useLocation } from "react-router-dom";
import api from "./lib/api.ts";

const CountdownTimer = ({ carCount, startDate, carTitle }) => {
  const location = useLocation();
  const hideHeader = location.pathname === "/auctions"; // hide div on Auctiond page

  const { language } = useLanguage();
  const daysRef = useRef(null);
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);
  const dayHeadingRef = useRef(null);
  const dateRef = useRef(null);

  // State for API data
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [lanesData, setLanesData] = useState([
    { id: 1, name: "Lane 1", totalCars: 0 },
    { id: 2, name: "Lane 2", totalCars: 0 },
    { id: 3, name: "Lane 3", totalCars: 0 },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch auction data and calculate total vehicles
  useEffect(() => {
    const fetchAuctionData = async () => {
      try {
        setIsLoading(true);

        // Try to fetch from API using the existing service
        try {
          // Try multiple endpoints to find auction data
          let data = null;
          let total = 0;

          try {
            // First try: /auctions endpoint
            const response = await api.get("/auctions");
            data = response.data;
          } catch (endpoint1Error) {
            console.warn(
              "First endpoint failed, trying alternatives:",
              endpoint1Error
            );

            try {
              // Second try: /vehicles endpoint
              const vehiclesResponse = await api.get("/vehicles");
              data = vehiclesResponse.data;
            } catch (endpoint2Error) {
              console.warn(
                "Second endpoint failed, trying alternatives:",
                endpoint2Error
              );

              try {
                // Third try: /auction-lanes endpoint
                const lanesResponse = await api.get("/auction-lanes");
                data = lanesResponse.data;
                } catch (endpoint3Error) {
                console.warn(
                  "All endpoints failed, using fallback:",
                  endpoint3Error
                );
                throw endpoint3Error;
              }
            }
          }

          // Calculate total vehicles from auctionLaneVehicles and extract lanes data
          if (data && Array.isArray(data)) {
            data.forEach((auction) => {
              if (
                auction.auctionLaneVehicles &&
                Array.isArray(auction.auctionLaneVehicles)
              ) {
                total += auction.auctionLaneVehicles.length;
              }
            });
            setTotalVehicles(total);

            // Extract lanes data from auctions
            const lanes = data.reduce((acc, auction) => {
              if (auction.auctionLanes && Array.isArray(auction.auctionLanes)) {
                auction.auctionLanes.forEach((lane) => {
                  const existingLane = acc.find((l) => l.id === lane.id);
                  if (existingLane) {
                    existingLane.totalCars += lane.vehicles?.length || 0;
                  } else {
                    acc.push({
                      id: lane.id,
                      name: lane.name || `Lane ${acc.length + 1}`,
                      totalCars: lane.vehicles?.length || 0,
                    });
                  }
                });
              }
              return acc;
            }, []);

            if (lanes.length > 0) {
              setLanesData(lanes);
            }
          } else if (
            data &&
            data.auctionLaneVehicles &&
            Array.isArray(data.auctionLaneVehicles)
          ) {
            // If data is a single auction object
            total = data.auctionLaneVehicles.length;
            setTotalVehicles(total);

            // Extract lanes data from single auction
            if (data.auctionLanes && Array.isArray(data.auctionLanes)) {
              const lanes = data.auctionLanes.map((lane, index) => ({
                id: lane.id || index,
                name: lane.name || `Lane ${index + 1}`,
                totalCars: lane.vehicles?.length || 0,
              }));
              setLanesData(lanes);
            }
          } else if (data && Array.isArray(data)) {
            // If data is an array of vehicles or other objects
            total = data.length;
            setTotalVehicles(total);

            // If we have vehicles data, create default lanes
            if (total > 0) {
              const defaultLanes = [
                { id: 1, name: "Lane 1", totalCars: Math.ceil(total / 3) },
                { id: 2, name: "Lane 2", totalCars: Math.ceil(total / 3) },
                {
                  id: 3,
                  name: "Lane 3",
                  totalCars: total - Math.ceil(total / 3) * 2,
                },
              ];
              setLanesData(defaultLanes);
            }
          }

        } catch (apiError) {
          console.warn(
            "All API endpoints failed, using fallback data:",
            apiError
          );
          // Fallback: use carCount prop if API fails
          setTotalVehicles(carCount || 0);

          // Set default lanes data when API fails
          if (carCount && carCount > 0) {
            const defaultLanes = [
              { id: 1, name: "Lane 1", totalCars: Math.ceil(carCount / 3) },
              { id: 2, name: "Lane 2", totalCars: Math.ceil(carCount / 3) },
              {
                id: 3,
                name: "Lane 3",
                totalCars: carCount - Math.ceil(carCount / 3) * 2,
              },
            ];
            setLanesData(defaultLanes);
          } else {
            setLanesData([
              { id: 1, name: "Lane 1", totalCars: 0 },
              { id: 2, name: "Lane 2", totalCars: 0 },
              { id: 3, name: "Lane 3", totalCars: 0 },
            ]);
          }
        }
      } catch (error) {
        console.error("Error fetching auction data:", error);
        // Fallback: use carCount prop if everything fails
        setTotalVehicles(carCount || 0);

        // Set default lanes data when everything fails
        if (carCount && carCount > 0) {
          const defaultLanes = [
            { id: 1, name: "Lane 1", totalCars: Math.ceil(carCount / 3) },
            { id: 2, name: "Lane 2", totalCars: Math.ceil(carCount / 3) },
            {
              id: 3,
              name: "Lane 3",
              totalCars: carCount - Math.ceil(carCount / 3) * 2,
            },
          ];
          setLanesData(defaultLanes);
        } else {
          setLanesData([
            { id: 1, name: "Lane 1", totalCars: 0 },
            { id: 2, name: "Lane 2", totalCars: 0 },
            { id: 3, name: "Lane 3", totalCars: 0 },
          ]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuctionData();
  }, [carCount]);

  useEffect(() => {
    const days = daysRef.current;
    const hours = hoursRef.current;
    const minutes = minutesRef.current;
    const seconds = secondsRef.current;

    let timeLeft = {
      d: 0,
      h: 0,
      m: 0,
      s: 0,
    };

    const targetDateTime = new Date(startDate);
    if (isNaN(targetDateTime.getTime())) {
      return;
    }
    if (isNaN(targetDateTime.getTime())) {
      return;
    }

    const dateHeading = dateRef.current;

    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const weekday = targetDateTime.toLocaleDateString("en-US", {
      weekday: "long",
    });
    if (dayHeadingRef.current) {
      dayHeadingRef.current.textContent = `${weekday} ${translations[language].liveAuctions.auction}`;
    }

    const day = targetDateTime.getDate();
    const suffix = getOrdinalSuffix(day);
    const month = targetDateTime.toLocaleString("default", { month: "long" });
    const year = targetDateTime.getFullYear();
    const time = targetDateTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    if (dateHeading) {
      dateHeading.textContent = `${day}${suffix} ${month} ${year} | ${time}`;
    }

    let totalSeconds = Math.floor((targetDateTime - new Date()) / 1000);
    setTimeLeft();

    const interval = setInterval(() => {
      if (totalSeconds < 0) {
        clearInterval(interval);
        return;
      }
      countTime();
    }, 1000);

    function setTimeLeft() {
      timeLeft.d = Math.floor(totalSeconds / (60 * 60 * 24));
      timeLeft.h = Math.floor((totalSeconds / (60 * 60)) % 24);
      timeLeft.m = Math.floor((totalSeconds / 60) % 60);
      timeLeft.s = Math.floor(totalSeconds % 60);
    }

    function countTime() {
      if (totalSeconds > 0) {
        timeLeft.s--;
        if (timeLeft.m >= 0 && timeLeft.s < 0) {
          timeLeft.s = 59;
          timeLeft.m--;
          if (timeLeft.h >= 0 && timeLeft.m < 0) {
            timeLeft.m = 59;
            timeLeft.h--;
            if (timeLeft.d >= 0 && timeLeft.h < 0) {
              timeLeft.h = 23;
              timeLeft.d--;
            }
          }
        }
      }
      totalSeconds--;
      printTime();
    }

    function printTime() {
      animateFlip(days, timeLeft.d);
      animateFlip(hours, timeLeft.h);
      animateFlip(minutes, timeLeft.m);
      animateFlip(seconds, timeLeft.s);
    }

    function animateFlip(element, value) {
      const bottomBack = element.querySelector(".bottom-back span");
      const topBack = element.querySelector(".top-back span");
      const top = element.querySelector(".top");
      const bottom = element.querySelector(".bottom");

      const currentValue = value < 10 ? `0${value}` : `${value}`;
      if (bottomBack.innerText === currentValue) return;

      topBack.innerText = currentValue;
      bottomBack.innerText = currentValue;

      gsap.to(top, {
        duration: 0.7,
        rotationX: -180,
        transformPerspective: 300,
        ease: "quart.out",
        onComplete: () => {
          top.innerText = currentValue;
          bottom.innerText = currentValue;
          gsap.set(top, { rotationX: 0 });
        },
      });

      gsap.to(element.querySelector(".top-back"), {
        duration: 0.7,
        rotationX: 0,
        transformPerspective: 300,
        ease: "quart.out",
        clearProps: "all",
      });
    }

    return () => clearInterval(interval);
  }, [startDate, language]);

  return (
    <>
      <main className=" text-[14px] leading-[1] font-poppins">
        <div className="bg-[#FFFFFF1F]  p-2  border-[2px] border-[#FFFFFF3D]">
          {!hideHeader && (
            <div className="ml-[0px]">
              <h1 className="dayHeading text-[20px] font-[28px] mb-[3px] text-[#ffffff]">
                <span className="font-[500]">{carTitle}</span>
              </h1>

              <h2
                className="text-[16px] text-[#cfcfcf] leading-[40px] mb-2 date font-[500]"
                ref={dateRef}
              >
                {typeof startDate === "string" || typeof startDate === "number"
                  ? new Date(startDate).toLocaleString("en-US", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : ""}
              </h2>

              <hr className="border-t-2 border-[#F15A29] w-[220px] mb-3" />

              <div className=" mb-[15px] flex items-center gap-x-2">
                <svg
                  width="27"
                  height="27"
                  viewBox="0 0 33 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z"
                    fill="white"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M16.3895 10.525C16.9536 10.525 17.4109 10.9822 17.4109 11.5463V16.5705L20.5163 19.6759C20.9151 20.0747 20.9151 20.7214 20.5163 21.1203C20.1174 21.5191 19.4707 21.5191 19.0719 21.1203L15.6673 17.7158C15.4758 17.5242 15.3682 17.2644 15.3682 16.9935V11.5463C15.3682 10.9822 15.8255 10.525 16.3895 10.525Z"
                    fill="#F15A29"
                  />
                  <path
                    d="M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z"
                    stroke="#F15A29"
                    stroke-width="1.02135"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M16.3895 10.525C16.9536 10.525 17.4109 10.9822 17.4109 11.5463V16.5705L20.5163 19.6759C20.9151 20.0747 20.9151 20.7214 20.5163 21.1203C20.1174 21.5191 19.4707 21.5191 19.0719 21.1203L15.6673 17.7158C15.4758 17.5242 15.3682 17.2644 15.3682 16.9935V11.5463C15.3682 10.9822 15.8255 10.525 16.3895 10.525Z"
                    stroke="#F15A29"
                    stroke-width="1.02135"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <h1 className="text-[20px] font-[500]">
                  <span className=" text-[#DE542E] font-[400]">
                    {translations[language].liveAuctions.auction}{" "}
                  </span>
                  {translations[language].counter.startIn}
                </h1>
              </div>
            </div>
          )}
          <div className=" text-center containDigits">
            <div className="  ml-[8px] ">
              <div className="content">
                <div
                  className="w-full bg-transparent flex flex-wrap justify-center items-center"
                  dir="ltr"
                >
                  {/* Days */}
                  <div className="flex flex-col items-center mr-[10px]">
                    <span
                      className="days time-elem   text-[4em]
    max-[480px]:text-[3em]
    max-[400px]:text-[2em]"
                      ref={daysRef}
                    >
                      <span className="top text-[40px] sm:text-[40px]">00</span>
                      <span className="top-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                      <span className="bottom text-[40px] sm:text-[40px]">
                        00
                      </span>
                      <span className="bottom-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                    </span>
                    <span className="text-[#ffffff] text-[12px] uppercase font-light">
                      {translations[language].common.time.days}
                    </span>
                  </div>

                  {/* Hours */}
                  <div className="flex flex-col items-center mr-[10px]">
                    <span className="hours time-elem" ref={hoursRef}>
                      <span className="top text-[40px] sm:text-[40px]">00</span>
                      <span className="top-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                      <span className="bottom text-[40px] sm:text-[40px]">
                        00
                      </span>
                      <span className="bottom-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                    </span>
                    <span className="text-[#ffffff] text-[12px] uppercase font-light">
                      {translations[language].common.time.hours}
                    </span>
                  </div>

                  {/* Minutes */}
                  <div className="flex flex-col items-center mr-[10px]">
                    <span className="minutes time-elem" ref={minutesRef}>
                      <span className="top text-[40px] sm:text-[40px]">00</span>
                      <span className="top-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                      <span className="bottom text-[40px] sm:text-[40px]">
                        00
                      </span>
                      <span className="bottom-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                    </span>
                    <span className="text-[#ffffff] text-[12px] uppercase font-light">
                      {translations[language].common.time.minutes}
                    </span>
                  </div>

                  {/* Seconds */}
                  <div className="flex flex-col items-center mr-[10px]">
                    <span className="seconds time-elem" ref={secondsRef}>
                      <span className="top text-[40px] sm:text-[40px]">00</span>
                      <span className="top-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                      <span className="bottom text-[40px] sm:text-[40px]">
                        00
                      </span>
                      <span className="bottom-back">
                        <span className="text-[40px] sm:text-[40px]">00</span>
                      </span>
                    </span>
                    <span className="text-[#ffffff] text-[12px] uppercase font-light">
                      {translations[language].common.time.seconds}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col justify-evenly h-full gap-2 mt-5 mb-5 ml-2">
                  <div className="font-[300] flex items-center gap-2">
                    <svg
                      width="25"
                      height="25"
                      viewBox="0 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.3053 22.878C17.9461 22.878 22.5189 20.8203 22.5189 18.2819C22.5189 16.9882 21.3311 15.8194 19.4195 14.9841C18.253 17.1247 16.4742 18.9698 14.2148 19.9354C13.0003 20.4544 11.6104 20.4544 10.3959 19.9354C8.13645 18.9698 6.35765 17.1247 5.19118 14.9841C3.27958 15.8194 2.0918 16.9882 2.0918 18.2819C2.0918 20.8203 6.66456 22.878 12.3053 22.878Z"
                        fill="#F15A29"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.15625 9.10443C5.15625 5.42967 8.35718 2.45068 12.3057 2.45068C16.2543 2.45068 19.4552 5.42967 19.4552 9.10443C19.4552 12.7504 17.1733 17.0049 13.6131 18.5263C12.7832 18.881 11.8283 18.881 10.9983 18.5263C7.43812 17.0049 5.15625 12.7504 5.15625 9.10443ZM12.3057 11.6429C13.4339 11.6429 14.3484 10.7283 14.3484 9.60016C14.3484 8.47201 13.4339 7.55746 12.3057 7.55746C11.1776 7.55746 10.263 8.47201 10.263 9.60016C10.263 10.7283 11.1776 11.6429 12.3057 11.6429Z"
                        fill="#F15A29"
                      />
                    </svg>
                    <p className="text-[14px] text-[white]-200 max-[490px]:text-[10px]">
                      {translations[language].counter.location}
                    </p>
                  </div>
                  <div className="font-[300] flex items-center gap-2">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.82324 6.219C8.65882 4.72005 9.07661 3.97058 9.70123 3.97058C10.3258 3.97058 10.7436 4.72005 11.5792 6.21899L11.7954 6.60679C12.0328 7.03274 12.1515 7.24572 12.3367 7.38624C12.5218 7.52677 12.7523 7.57893 13.2134 7.68325L13.6332 7.77823C15.2558 8.14536 16.0671 8.32892 16.2601 8.94962C16.4531 9.57031 15.9 10.2171 14.7938 11.5106L14.5077 11.8452C14.1933 12.2128 14.0362 12.3966 13.9654 12.624C13.8947 12.8514 13.9185 13.0966 13.966 13.587L14.0093 14.0335C14.1765 15.7593 14.2601 16.6223 13.7548 17.0059C13.2495 17.3895 12.4899 17.0397 10.9707 16.3402L10.5776 16.1593C10.1459 15.9605 9.93004 15.8611 9.70123 15.8611C9.47242 15.8611 9.25656 15.9605 8.82484 16.1593L8.4318 16.3402C6.91257 17.0397 6.15296 17.3895 5.64763 17.0059C5.1423 16.6223 5.22592 15.7593 5.39316 14.0335L5.43643 13.587C5.48395 13.0966 5.50771 12.8514 5.43701 12.624C5.3663 12.3966 5.20913 12.2128 4.89479 11.8452L4.60861 11.5106C3.50244 10.2171 2.94935 9.57031 3.14237 8.94962C3.33539 8.32892 4.14668 8.14536 5.76927 7.77823L6.18905 7.68325C6.65014 7.57893 6.88068 7.52677 7.06579 7.38624C7.2509 7.24572 7.36963 7.03274 7.60707 6.60679L7.82324 6.219Z"
                        fill="#E95923"
                      />
                      <path
                        d="M3.7777 2.03071C3.80097 1.94861 3.94669 1.94812 3.97052 2.03006C4.07933 2.40422 4.28109 2.95727 4.58619 3.26032C4.89129 3.56336 5.44569 3.76138 5.82058 3.86766C5.90268 3.89094 5.90317 4.03666 5.82123 4.06049C5.44707 4.1693 4.89402 4.37106 4.59098 4.67616C4.28793 4.98126 4.08991 5.53566 3.98363 5.91055C3.96036 5.99264 3.81463 5.99314 3.79081 5.9112C3.682 5.53704 3.48023 4.98399 3.17513 4.68094C2.87003 4.3779 2.31563 4.17988 1.94074 4.0736C1.85865 4.05032 1.85816 3.9046 1.94009 3.88077C2.31425 3.77196 2.86731 3.5702 3.17035 3.2651C3.4734 2.96 3.67142 2.4056 3.7777 2.03071Z"
                        fill="#E95923"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14.7458 2.61255C15.0673 2.61255 15.3279 2.87315 15.3279 3.19461V3.38864H15.5219C15.8434 3.38864 16.104 3.64923 16.104 3.9707C16.104 4.29217 15.8434 4.55276 15.5219 4.55276H15.3279V4.74679C15.3279 5.06825 15.0673 5.32885 14.7458 5.32885C14.4244 5.32885 14.1638 5.06825 14.1638 4.74679V4.55276H13.9698C13.6483 4.55276 13.3877 4.29217 13.3877 3.9707C13.3877 3.64923 13.6483 3.38864 13.9698 3.38864H14.1638V3.19461C14.1638 2.87315 14.4244 2.61255 14.7458 2.61255Z"
                        fill="#E95923"
                      />
                    </svg>
                    <p className="text-[14px] text-[white]-200 max-[490px]:text-[10px]">
                      {translations[language].counter.majorityGreenLight}
                    </p>
                  </div>
                  <div className="font-[300] flex items-center gap-2">
                    <svg
                      width="24"
                      height="19"
                      viewBox="0 0 24 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M23.0289 7.51646H21.9948L19.3329 1.53196C19.2136 1.26077 19.0179 1.03025 18.7695 0.868677C18.5212 0.707101 18.2312 0.621484 17.9349 0.622321H6.67447C6.37822 0.621484 6.08816 0.707101 5.83984 0.868677C5.59152 1.03025 5.39574 1.26077 5.27649 1.53196L2.61459 7.51646H1.58047C1.37731 7.51646 1.18247 7.59717 1.03881 7.74082C0.895158 7.88448 0.814453 8.07932 0.814453 8.28248C0.814453 8.48564 0.895158 8.68048 1.03881 8.82413C1.18247 8.96779 1.37731 9.04849 1.58047 9.04849H2.34648V16.7087C2.34648 17.115 2.50789 17.5046 2.79521 17.792C3.08252 18.0793 3.4722 18.2407 3.87852 18.2407H6.17656C6.58288 18.2407 6.97256 18.0793 7.25987 17.792C7.54718 17.5046 7.7086 17.115 7.7086 16.7087V15.1766H16.9008V16.7087C16.9008 17.115 17.0622 17.5046 17.3495 17.792C17.6368 18.0793 18.0265 18.2407 18.4328 18.2407H20.7309C21.1372 18.2407 21.5269 18.0793 21.8142 17.792C22.1015 17.5046 22.2629 17.115 22.2629 16.7087V9.04849H23.0289C23.2321 9.04849 23.4269 8.96779 23.5706 8.82413C23.7142 8.68048 23.7949 8.48564 23.7949 8.28248C23.7949 8.07932 23.7142 7.88448 23.5706 7.74082C23.4269 7.59717 23.2321 7.51646 23.0289 7.51646ZM7.7086 12.1126H6.17656C5.9734 12.1126 5.77856 12.0319 5.63491 11.8882C5.49125 11.7445 5.41055 11.5497 5.41055 11.3465C5.41055 11.1434 5.49125 10.9485 5.63491 10.8049C5.77856 10.6612 5.9734 10.5805 6.17656 10.5805H7.7086C7.91176 10.5805 8.10659 10.6612 8.25025 10.8049C8.39391 10.9485 8.47461 11.1434 8.47461 11.3465C8.47461 11.5497 8.39391 11.7445 8.25025 11.8882C8.10659 12.0319 7.91176 12.1126 7.7086 12.1126ZM16.9008 12.1126C16.6976 12.1126 16.5028 12.0319 16.3591 11.8882C16.2155 11.7445 16.1348 11.5497 16.1348 11.3465C16.1348 11.1434 16.2155 10.9485 16.3591 10.8049C16.5028 10.6612 16.6976 10.5805 16.9008 10.5805H18.4328C18.636 10.5805 18.8308 10.6612 18.9745 10.8049C19.1181 10.9485 19.1988 11.1434 19.1988 11.3465C19.1988 11.5497 18.1181 11.7445 18.9745 11.8882C18.8308 12.0319 18.636 12.1126 18.4328 12.1126H16.9008Z"
                        fill="#F15A29"
                      />
                    </svg>
                    <p className="text-[white] text-[14px] font-[300] max-[490px]:text-[10px]">
                      {isLoading ? (
                        <span className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Loading...
                        </span>
                      ) : totalVehicles > 0 ? (
                        `${totalVehicles} ${translations[language].counter.carsInAuction}`
                      ) : (
                        `${carCount || 0} ${
                          translations[language].counter.carsInAuction
                        }`
                      )}
                    </p>
                  </div>
                </div>

                <div className="w-full flex flex-row justify-between gap-1 mt-4">
                  {(Array.isArray(lanesData)
                    ? lanesData
                    : [
                        { name: "Lane 1", totalCars: 0 },
                        { name: "Lane 2", totalCars: 0 },
                        { name: "Lane 3", totalCars: 0 },
                      ]
                  ).map((lane, idx) => (
                    <div
                      key={lane.id || lane.name || idx}
                      className="flex bg-black p-2 rounded-lg flex-col items-center gap-2 flex-1"
                    >
                      <div className="w-full flex flex-col items-center mb-1">
                        <span className="text-white  font-semibold">
                          {lane.name || `Lane ${idx + 1}`} (
                          {lane.totalCars ?? 0} )
                        </span>
                        <span className="text-white text-[11px] font-light"></span>
                      </div>
                      <button
                        className="text-white border-[1px] border-[#FFFFFF3D] bg-gray-800 
                          hover:bg-[#272727c5] hover:border-[#0e0b0b] 
                          text-[10px] tracking-widest cursor-pointer
                          w-[95%] h-[25px] sm:w-[90%] sm:h-[40px] sm:text-[14px]"
                      >
                        {translations[language].auctionControls.viewList}
                      </button>
                      <button
                        className="text-white bg-[#DE542E] hover:bg-[#de542ee5] hover:border-[2px] hover:border-[#180e0e] 
                          text-[10px] font-semibold border-none cursor-pointer
                          w-[90%] h-[25px] sm:w-[90%] sm:h-[40px] sm:text-[14px]"
                      >
                        {translations[language].auctionControls.joinAuction}
                      </button>
                    </div>
                  ))}
                </div>

                {/* <div className="mt-[35px] flex flex-wrap justify-between">
                  <button
                    className="text-white border-[2px] border-[#FFFFFF3D] bg-[#0000004D] 
             hover:bg-[#272727c5] hover:border-[#0e0b0b] 
              text-[10px] font-semibold cursor-pointer
             w-[100px] h-[25px] sm:w-[100px] sm:h-[50px] sm:text-[14px]"
                  >
                    {translations[language].auctionControls.viewList}
                  </button>
                  <button
                    className="text-white bg-[#DE542E] hover:bg-[#de542ee5] hover:border-[2px] hover:border-[#180e0e] 
   text-[10px] font-semibold border-none cursor-pointer
  w-[100px] h-[25px] sm:w-[100px] sm:h-[50px] sm:text-[14px]"
                  >
                    {translations[language].auctionControls.joinAuction}
                  </button>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default CountdownTimer;
